import { Capacitor } from '@capacitor/core';
import { deviceService } from '../services/api';

// Real push, via FCM - complements localNotifications.js rather than
// replacing it: local notifications fire from live in-app events (a
// websocket message arriving while the app is open/backgrounded but still
// running), while this is what reaches the device when the app process
// isn't running at all. Requires FIREBASE_CREDENTIALS_JSON configured on
// the backend (see users/push.py) plus the native Firebase config files
// (google-services.json / GoogleService-Info.plist) - without those this
// still runs, it just never receives a token to register.
let currentToken = null;

/** Requests permission, registers with FCM/APNs, and sends the resulting
 * token to the backend so it can actually receive pushes. Native-only. */
export async function initPushNotifications() {
  if (!Capacitor.isNativePlatform()) return;

  const { PushNotifications } = await import('@capacitor/push-notifications');

  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === 'prompt') {
    permission = await PushNotifications.requestPermissions();
  }
  if (permission.receive !== 'granted') return;

  await PushNotifications.addListener('registration', (token) => {
    currentToken = token.value;
    const platform = Capacitor.getPlatform() === 'ios' ? 'ios' : 'android';
    deviceService.registerToken(token.value, platform).catch(() => {
      // Non-fatal: the app still works without push, and the next launch
      // (or the periodic re-register a real app would add) tries again.
    });
  });

  await PushNotifications.addListener('registrationError', (error) => {
    console.error('Push registration failed', error);
  });

  await PushNotifications.register();
}

/** Unregisters this device's token from the backend - call on logout so a
 * different account signing in on the same device doesn't inherit pushes
 * meant for whoever was logged in before them. */
export async function unregisterPushNotifications() {
  if (!Capacitor.isNativePlatform() || !currentToken) return;
  try {
    await deviceService.unregisterToken(currentToken);
  } catch (e) {
    // Non-fatal - a stale token is harmless (send_push_notification drops
    // it server-side the first time FCM reports it as invalid).
  }
  currentToken = null;
}

/** Registers a tap handler for when the user opens a push from the tray
 * (app backgrounded or killed). Returns an unsubscribe function. */
export async function onPushNotificationTap(callback) {
  if (!Capacitor.isNativePlatform()) return () => {};

  const { PushNotifications } = await import('@capacitor/push-notifications');
  const handle = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    callback(action.notification?.data || {});
  });
  return () => handle.remove();
}

/** Registers a handler for a push arriving while the app is in the
 * foreground - FCM/APNs don't show a system banner for these on their own,
 * so the app has to react itself (e.g. render it as a local notification).
 * Returns an unsubscribe function. */
export async function onPushNotificationReceived(callback) {
  if (!Capacitor.isNativePlatform()) return () => {};

  const { PushNotifications } = await import('@capacitor/push-notifications');
  const handle = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
    callback(notification);
  });
  return () => handle.remove();
}
